import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { BookOpen, ArrowRight, Search, CheckCircle2, Circle, LayoutGrid, Rows3, Image as ImageIcon, Trophy, Clock } from "lucide-react";
import { useDragSelect } from "@/lib/useDragSelect";
import { setQueue } from "@/lib/practiceQueue";
import SelectionTray from "@/components/SelectionTray";

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; }
};

const secondsToMin = (s) => Math.max(1, Math.round((s || 900) / 60));

const chapterTone = (name) => {
  // Deterministic hue per chapter so students recognise chapter colour at a glance.
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 70%, 45%)`;
};

export default function PitmanList() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [chapter, setChapter] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("stenosync.pitman.view") || "table");
  const listRef = useRef(null);
  const { selected, dragging, rect, toggle, clear } = useDragSelect(listRef);

  useEffect(() => {
    Promise.all([
      api.get("/pitman-exercises"),
      api.get("/attempts/summary?module=pitman").catch(() => ({ data: {} })),
    ]).then(([lp, sm]) => {
      setItems(lp.data || []);
      setSummary(sm.data || {});
    }).finally(() => setLoading(false));
  }, []);

  const changeView = (v) => { setView(v); try { localStorage.setItem("stenosync.pitman.view", v); } catch {} };

  const chapters = useMemo(() => {
    const s = new Set(items.map((i) => i.chapter));
    return ["all", ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((p) => {
      if (chapter !== "all" && p.chapter !== chapter) return false;
      if (query && !p.title.toLowerCase().includes(query) && !(p.chapter || "").toLowerCase().includes(query)) return false;
      return true;
    });
  }, [items, q, chapter]);

  // Per-chapter progress — "X of Y completed" so students can gauge how far they've got.
  const chapterProgress = useMemo(() => {
    const map = {};
    for (const it of items) {
      const c = it.chapter || "Unassigned";
      if (!map[c]) map[c] = { total: 0, done: 0 };
      map[c].total++;
      if (summary[it.id] && summary[it.id].attempts > 0) map[c].done++;
    }
    return map;
  }, [items, summary]);

  const overallDone = Object.values(chapterProgress).reduce((a, x) => a + x.done, 0);
  const overallTotal = items.length;
  const overallPct = overallTotal ? Math.round((overallDone / overallTotal) * 100) : 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-12">

        {/* Hero header */}
        <section className="mb-8 grid md:grid-cols-[1fr_auto] gap-6 items-end">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-3">Module 03 · Pitman Shorthand</div>
            <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4" style={{ color: "var(--brand-navy)" }}>
              <BookOpen className="w-9 h-9" /> Pitman Steno Exercises
            </h1>
            <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
              Practice each Pitman exercise: read the shorthand on the left, transcribe on the right, get instant accuracy + WPM.
            </p>
          </div>

          {/* Overall progress card */}
          <div className="border border-border rounded-xl bg-card shadow-sm px-5 py-4 min-w-[260px]" data-testid="pitman-overall-progress">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Your progress
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-3xl font-black" style={{ color: "var(--brand-navy)" }}>{overallDone}</span>
              <span className="font-mono text-muted-foreground">/ {overallTotal} completed</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-brand-gold transition-all duration-500" style={{ width: `${overallPct}%` }} />
            </div>
            <div className="mt-1 text-[10px] font-mono text-muted-foreground text-right">{overallPct}%</div>
          </div>
        </section>

        {/* Toolbar — sticky so search stays reachable while scrolling long lists */}
        <div className="sticky top-16 z-30 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-b border-border/60 mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exercise or chapter…" className="rounded-lg h-11 pl-9 font-mono border-border bg-card" data-testid="pitman-search" />
          </div>
          <div className="inline-flex border border-border rounded-lg overflow-hidden" role="tablist" aria-label="View mode">
            <button type="button" onClick={() => changeView("table")} data-testid="pitman-view-table" className={`px-3 py-2 flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${view === "table" ? "bg-brand-gold text-white" : "bg-card text-muted-foreground hover:bg-secondary"}`}>
              <Rows3 className="w-3.5 h-3.5" /> Table
            </button>
            <button type="button" onClick={() => changeView("cards")} data-testid="pitman-view-cards" className={`px-3 py-2 flex items-center gap-1.5 border-l border-border text-[10px] uppercase tracking-widest font-bold transition-colors ${view === "cards" ? "bg-brand-gold text-white" : "bg-card text-muted-foreground hover:bg-secondary"}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Cards
            </button>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground hidden md:block">
            Tip · Drag to select multiple · Shift/Ctrl for extra
          </div>
        </div>

        {/* Chapter chips — coloured per chapter, with progress ratio */}
        {chapters.length > 1 && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1" role="tablist" aria-label="Chapter filter">
            {chapters.map((c) => {
              const isAll = c === "all";
              const pr = !isAll ? chapterProgress[c] : { done: overallDone, total: overallTotal };
              const active = chapter === c;
              return (
                <button
                  key={c}
                  onClick={() => setChapter(c)}
                  data-testid={`pitman-chapter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`shrink-0 flex items-center gap-2 px-3 h-9 rounded-full border-2 transition-all text-[11px] font-bold whitespace-nowrap ${
                    active ? "text-white shadow-md" : "bg-card hover:shadow-sm text-foreground"
                  }`}
                  style={active ? { background: isAll ? "var(--brand-navy)" : chapterTone(c), borderColor: isAll ? "var(--brand-navy)" : chapterTone(c) } : { borderColor: isAll ? "var(--border)" : chapterTone(c) + "55" }}
                >
                  {!active && <span className="w-2 h-2 rounded-full" style={{ background: isAll ? "var(--brand-navy)" : chapterTone(c) }} />}
                  <span className="uppercase tracking-widest">{isAll ? "All chapters" : c}</span>
                  {pr && pr.total > 0 && (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${active ? "bg-white/20" : "bg-secondary text-muted-foreground"}`}>
                      {pr.done}/{pr.total}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div ref={listRef} className="relative select-none" data-testid="pitman-list-region">
          {loading ? (
            <div className="border border-border rounded-xl bg-card p-10 text-center font-mono text-muted-foreground">Loading exercises…</div>
          ) : filtered.length === 0 ? (
            <div className="border-2 border-dashed border-border p-10 text-center font-mono text-muted-foreground rounded-xl bg-card" data-testid="pitman-empty">
              {items.length === 0 ? "No exercises yet." : "No exercises match your filters."}
            </div>
          ) : view === "table" ? (
            <PitmanTable items={filtered} summary={summary} selected={selected} onToggle={toggle} />
          ) : (
            <PitmanCards items={filtered} summary={summary} selected={selected} onToggle={toggle} />
          )}

          {/* Drag-marquee overlay */}
          {dragging && rect && (
            <div
              className="fixed pointer-events-none border-2 border-brand-gold bg-brand-gold/10 rounded"
              style={{ left: rect.left, top: rect.top, width: rect.right - rect.left, height: rect.bottom - rect.top }}
              data-testid="drag-marquee"
            />
          )}
        </div>

        <SelectionTray
          count={selected.size}
          onClear={clear}
          onStart={() => setQueue("pitman", Array.from(selected))}
          firstHref={selected.size ? `/pitman/${Array.from(selected)[0]}` : "#"}
          moduleLabel="Pitman"
        />
      </main>
    </div>
  );
}

function PitmanTable({ items, summary, selected, onToggle }) {
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card shadow-sm" data-testid="pitman-table-wrap">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="pitman-table">
          <thead className="bg-secondary/60 border-b border-border">
            <tr className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              <th className="px-3 py-3 text-left w-10"></th>
              <th className="px-4 py-3 text-left w-14">#</th>
              <th className="px-4 py-3 text-left w-20">Shorthand</th>
              <th className="px-4 py-3 text-left">Exercise</th>
              <th className="px-4 py-3 text-left w-32">Chapter</th>
              <th className="px-4 py-3 text-right w-20">Words</th>
              <th className="px-4 py-3 text-right w-20">Time</th>
              <th className="px-4 py-3 text-left w-40">Status</th>
              <th className="px-4 py-3 text-right w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, idx) => {
              const s = summary[p.id];
              const attempted = s && s.attempts > 0;
              const thumb = p.images && p.images[0] ? `${backendBase}${p.images[0]}` : null;
              const isSel = selected?.has(p.id);
              return (
                <tr
                  key={p.id}
                  data-testid={`pitman-row-${p.id}`}
                  data-select-key={p.id}
                  className={`border-b border-border last:border-b-0 transition-colors group cursor-pointer ${isSel ? "bg-brand-gold/15" : "hover:bg-secondary/40"}`}
                >
                  <td className="px-3 py-3 align-middle" data-noselect>
                    <input
                      type="checkbox"
                      checked={!!isSel}
                      onChange={() => onToggle?.(p.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 accent-[var(--brand-gold)] cursor-pointer"
                      data-testid={`pitman-check-${p.id}`}
                      aria-label={`Select ${p.title}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground align-middle">{String(idx + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-3 align-middle">
                    {thumb ? (
                      <img src={thumb} alt="" className="w-12 h-12 object-cover rounded-md border border-border bg-secondary" />
                    ) : (
                      <div className="w-12 h-12 rounded-md border border-dashed border-border bg-secondary/60 flex items-center justify-center text-muted-foreground/60">
                        <ImageIcon className="w-5 h-5" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="font-bold text-foreground leading-tight">{p.title}</div>
                    {p.content && (
                      <div className="mt-0.5 text-xs font-mono text-muted-foreground/80 line-clamp-1">{p.content.slice(0, 120)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <span
                      className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest font-bold px-2 py-0.5 rounded"
                      style={{ background: chapterTone(p.chapter) + "18", color: chapterTone(p.chapter) }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: chapterTone(p.chapter) }} />
                      {p.chapter}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-middle text-right font-mono text-sm">{p.word_count || 0}</td>
                  <td className="px-4 py-3 align-middle text-right font-mono text-xs text-muted-foreground">
                    <Clock className="w-3 h-3 inline mr-1" />{secondsToMin(p.time_limit_seconds)} min
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <StatusPill attempted={attempted} accuracy={s?.best_accuracy} wpm={s?.best_wpm} />
                  </td>
                  <td className="px-4 py-3 align-middle text-right">
                    <Link
                      to={`/pitman/${p.id}`}
                      data-testid={`pitman-open-${p.id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-brand-gold hover:bg-[var(--brand-gold-hover)] text-white text-[11px] uppercase tracking-widest font-bold transition-colors"
                    >
                      {attempted ? "Retry" : "Practice"} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PitmanCards({ items, summary, selected, onToggle }) {
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pitman-list">
      {items.map((p) => {
        const s = summary[p.id];
        const attempted = s && s.attempts > 0;
        const thumb = p.images && p.images[0] ? `${backendBase}${p.images[0]}` : null;
        const isSel = selected?.has(p.id);
        return (
          <div
            key={p.id}
            data-testid={`pitman-item-${p.id}`}
            data-select-key={p.id}
            className={`group relative border rounded-xl overflow-hidden flex flex-col transition-all cursor-pointer ${
              isSel ? "border-brand-gold ring-2 ring-brand-gold/40 bg-brand-gold/5" : "border-border bg-card hover:border-brand-gold/60 hover:-translate-y-1 hover:shadow-lg"
            }`}
          >
            <input
              type="checkbox"
              checked={!!isSel}
              onChange={() => onToggle?.(p.id)}
              onClick={(e) => e.stopPropagation()}
              data-noselect
              data-testid={`pitman-check-${p.id}`}
              aria-label={`Select ${p.title}`}
              className="absolute top-3 left-3 w-4 h-4 accent-[var(--brand-gold)] cursor-pointer z-10"
            />
            {thumb ? (
              <div className="aspect-[16/9] bg-secondary overflow-hidden border-b border-border">
                <img src={thumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            ) : (
              <div className="aspect-[16/9] bg-secondary/60 flex items-center justify-center border-b border-border">
                <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
              </div>
            )}
            <div className="p-5 flex flex-col flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest font-bold px-2 py-0.5 rounded"
                  style={{ background: chapterTone(p.chapter) + "18", color: chapterTone(p.chapter) }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: chapterTone(p.chapter) }} />
                  {p.chapter}
                </span>
                <StatusPill attempted={attempted} accuracy={s?.best_accuracy} wpm={s?.best_wpm} compact />
              </div>
              <h3 className="text-lg font-black tracking-tight mt-2 leading-tight" style={{ color: "var(--brand-navy)" }}>{p.title}</h3>
              <p className="text-sm font-mono text-muted-foreground line-clamp-2 mt-2 flex-1">{p.content}</p>
              <div className="flex items-center justify-between mt-4 text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
                <span>{p.word_count} words · {secondsToMin(p.time_limit_seconds)} min</span>
                <Link
                  to={`/pitman/${p.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-brand-gold group-hover:underline underline-offset-4"
                  data-testid={`pitman-open-${p.id}`}
                >
                  {attempted ? "Retry" : "Practice"} <ArrowRight className="w-3.5 h-3.5 inline" />
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ attempted, accuracy, wpm, compact = false }) {
  if (!attempted) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${compact ? "px-1.5 py-0.5" : "px-2 py-1"} rounded-md text-[10px] font-mono uppercase tracking-widest font-bold bg-slate-100 text-slate-600 border border-slate-300`}>
        <Circle className="w-3 h-3" /> Pending
      </span>
    );
  }
  const tone =
    accuracy >= 90 ? "bg-emerald-50 text-emerald-700 border-emerald-300" :
    accuracy >= 70 ? "bg-sky-50 text-sky-700 border-sky-300" :
    "bg-rose-50 text-rose-700 border-rose-300";
  return (
    <span className={`inline-flex items-center gap-1.5 ${compact ? "px-1.5 py-0.5" : "px-2 py-1"} rounded-md text-[10px] font-mono uppercase tracking-widest font-bold border ${tone}`}>
      <CheckCircle2 className="w-3 h-3" /> {accuracy}%{!compact && ` · ${wpm} WPM`}
    </span>
  );
}
